/**
 * Pipeline-plane API client.
 *
 * Talks to the pipeline-api (`services/pipeline-api`) via the dev proxy at
 * /api/platform. Endpoints: tenants, connections, catalog (connector specs),
 * syncs, vault.
 *
 * Auth: relies on the bearer JWT held by AuthContext. The backend extracts
 * `organization_id` and `workspace_id` from the JWT claims; in dev_bypass
 * mode the backend falls back to env defaults.
 */

const APP_BASE = '/api/platform';
const HTTP_NO_CONTENT = 204;


function authHeaders() {
  // AuthContext stores the JWT under 'authToken' (see contexts/AuthContext.js).
  // Reading 'access_token' here used to silently send unauthenticated
  // requests, which made pipeline-api fall into dev_bypass and serve
  // the default tenant's data — a privacy leak. See git history.
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}


// Reshape a parsed error body's message/detail/error field into a display
// string: array bodies (e.g. FastAPI validation errors) are joined,
// non-strings are stringified, everything else passes through unchanged.
function _normalizePipelineErrorMessage(m) {
  if (Array.isArray(m)) {return m.map((d) => d.msg || JSON.stringify(d)).join('; ');}
  if (m != null && typeof m !== 'string') {return JSON.stringify(m);}
  return m;
}

// pipeline-api errors serialise as {code, message, details}; FastAPI's
// default validators use {detail}. Surface the human-readable field so
// toast messages don't show the raw `[METHOD /path] status: {json}` blob.
async function _buildPipelineError(res) {
  const text = await res.text().catch(() => '');
  let message = '';
  let code = null;
  let details = null;
  try {
    const parsed = JSON.parse(text);
    const raw = parsed.message ?? parsed.detail ?? parsed.error;
    message = _normalizePipelineErrorMessage(raw) || '';
    code = parsed.code || null;
    details = parsed.details || null;
  } catch {
    // Non-JSON body — fall back to the raw text.
    message = text;
  }
  if (!message) {message = `HTTP ${res.status}`;}
  const err = new Error(message);
  err.status = res.status;
  err.code = code;
  err.details = details;
  return err;
}

async function request(method, path, body) {
  const init = {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  };
  if (body !== undefined) {init.body = JSON.stringify(body);}

  const res = await fetch(`${APP_BASE}${path}`, init);
  if (!res.ok) {throw await _buildPipelineError(res);}
  if (res.status === HTTP_NO_CONTENT) {return null;}
  return res.json();
}


// ── Catalog (connector specs available for this tenant) ──────────────
export const listConnectorSpecs = () => request('GET', '/catalog');
// Map of connector_name -> { state: 'AVAILABLE'|'UNAVAILABLE', message?, since? }.
// Driven by Dagster code-location load status (Pipeline Management
// Start/Stop in vendor-console). Cached server-side ~15s.
export const listCatalogAvailability = () => request('GET', '/catalog/availability');
export const getConnectorSpec = (name, version) =>
  request('GET', `/catalog/${encodeURIComponent(name)}/${encodeURIComponent(version)}`);

// ── Connections (configured connector instances) ─────────────────────
export const listConnections = () => request('GET', '/connections');
export const getConnection = (id) => request('GET', `/connections/${id}`);
export const createConnection = (payload) => request('POST', '/connections', payload);
export const updateConnection = (id, payload) => request('PUT', `/connections/${id}`, payload);
export const deleteConnection = (id) => request('DELETE', `/connections/${id}`);
// Test Connection — proves credentials, estimates sync size (total +
// windowed by history_days), and pulls a handful of sample records per
// stream. Pass `streams` (array of names) to scope the test to a subset
// — used by the wizard to test one stream at a time.
//
// Pass `{ useSaved: true }` (UAT-only) to skip the live probe and return
// the previously saved `stream_test_result` unchanged. Surfaced through
// the "Test … (saved)" buttons that are gated by the UAT feature flag.
export const testConnection = (id, streams, { useSaved = false } = {}) => {
  const params = [];
  if (streams && streams.length) {
    for (const s of streams) {params.push(`streams=${encodeURIComponent(s)}`);}
  }
  if (useSaved) {params.push('use_saved=true');}
  const qs = params.length ? '?' + params.join('&') : '';
  return request('POST', `/connections/${encodeURIComponent(id)}/test-connection${qs}`);
};

// ── Bulk export / import ─────────────────────────────────────────────
export const fetchConnectionsImportTemplate = () =>
  request('GET', '/connections/import-template');
export const importConnections = (payload) =>
  request('POST', '/connections/import', payload);

// ── Destinations (BYO-DB warehouses) ────────────────────────────────
// A workspace targets exactly one warehouse for ingested data — either
// `postgres` or `redshift`. See docs/BYO_DATABASE_PLAN.md.
export const listDestinations = () => request('GET', '/destinations');

// ── Syncs (trigger + per-stream cancel) ──────────────────────────────
// All sync runs are launched as Dagster runs by the app; the legacy
// `/syncs` POST/cancel routes are thin shims that submit to Dagster
// (`DagsterRuntime`) while still writing the `sync_runs` audit row.
// For listing/inspecting runs use the Dagit adapters below; per-table
// timelines are built from those by `services/syncHistory.js`.
//
// Pass `{ useSaved: true }` (UAT-only) to make the orchestrator replay
// the cached Test Connection sample rows instead of calling the live
// source — drives the "Sync now — UAT" / "Sync all — UAT" buttons.
// `sampleLimit` (UAT-only, requires useSaved) tells the API to refresh
// the cached samples to that depth before launching the replay run.
export const triggerSync = (
  connectionId,
  onlyStream = null,
  { useSaved = false, sampleLimit = null } = {},
) =>
  request('POST', '/syncs', {
    connection_id: connectionId,
    ...(onlyStream ? { only_stream: onlyStream } : {}),
    ...(useSaved ? { use_saved_samples: true } : {}),
    ...(useSaved && sampleLimit ? { sample_limit: sampleLimit } : {}),
  });
export const cancelSyncStream = (id, stream) =>
  request('POST', `/syncs/${encodeURIComponent(id)}/streams/${encodeURIComponent(stream)}/cancel`);

// ── Syncs (Dagit-backed proxy: /syncs/dagit/*) ───────────────────────
// Canonical API for run history. Sourced live from the Dagster
// orchestrator's GraphQL endpoint and tenant-scoped via run tags.
// Returns Dagster-shaped fields (start_time, end_time, connector,
// materializations, etc.). The `*ViaDagster` adapters reshape the same
// data into the legacy SyncRun shape for callers that haven't been
// migrated to the native Dagster shape yet.
export const listDagitRuns = ({
  connectionId, connector, status, triggeredBy, layer, limit = 50,
} = {}) => {
  const qs = new URLSearchParams();
  if (connectionId) {qs.set('connection_id', connectionId);}
  if (connector) {qs.set('connector', connector);}
  if (status) {qs.set('status', status);}
  if (triggeredBy) {qs.set('triggered_by', triggeredBy);}
  if (layer) {qs.set('layer', layer);}
  if (limit) {qs.set('limit', String(limit));}
  const q = qs.toString();
  return request('GET', q ? `/syncs/dagit/runs?${q}` : '/syncs/dagit/runs');
};
export const getDagitRun = (runId) =>
  request('GET', `/syncs/dagit/runs/${encodeURIComponent(runId)}`);
// Live scheduling-machinery state: the Dagster SENSOR daemon health plus
// every per-connector `{connector}_schedule_sensor` RUNNING/STOPPED status.
// Returns { daemon, sensors[], summary: { total, running, stopped,
// scheduling_active }, error }. Used by the Schedule Timeline dialog to show
// whether scheduled syncs can actually fire.
export const getSchedulerStatus = () =>
  request('GET', '/syncs/dagit/scheduler-status');
// Start or stop a connector's `{connector}_schedule_sensor`. action is
// 'start' | 'stop'. Returns the refreshed scheduler-status payload.
// Enabling a stopped sensor is how a connector's cron schedules begin firing.
export const setScheduleSensor = (connector, action) =>
  request('POST', '/syncs/dagit/scheduler-status/sensor', { connector, action });
export const getDagitRunLogs = (runId, afterCursor = null) => {
  const qs = afterCursor ? `?after_cursor=${encodeURIComponent(afterCursor)}` : '';
  return request('GET', `/syncs/dagit/runs/${encodeURIComponent(runId)}/logs${qs}`);
};
// Submit a backfill: launches one Dagster run per (connection × day) in the
// given inclusive [from_date, to_date] range, optionally restricted to a
// subset of streams. Returns { launched, skipped, run_ids, error? }.
export const launchDagitBackfill = ({ connectionId, fromDate, toDate, streams }) =>
  request('POST', '/syncs/dagit/backfill', {
    connection_id: connectionId,
    from_date: fromDate,
    to_date: toDate,
    streams: streams && streams.length ? streams : null,
  });

// ── Dagster → legacy-shape adapters ──────────────────────────────────
// The original DataSourcesPage right-pane was built around the legacy
// `/syncs` shape (rows from Postgres `sync_runs`). Phase 7 cuts that
// pipeline over to Dagster, so the legacy table no longer accumulates
// rows and the page goes stale. These adapters re-shape Dagster's
// `/syncs/dagit/*` payloads into the legacy fields the right pane reads
// (`status`, `started_at`, `finished_at`, `rows_loaded`, `streams[]`),
// so the existing UI keeps working with no surgery.
//
// Status mapping. We intentionally distinguish "definitely terminal"
// states (success/failure/cancelled) from "definitely active" (running)
// from "limbo" (queued / starting / not_started / managed). Unknown
// statuses collapse to 'unknown' rather than 'queued' so the UI can
// flag them visibly instead of silently spinning forever — that was
// the source of "shows Running… for 3 hours after Dagster crashed".
const _DAGSTER_STATUS_TO_LEGACY = {
  success: 'success',
  failure: 'failed',
  started: 'running',
  starting: 'queued',
  queued: 'queued',
  not_started: 'queued',
  managed: 'queued',
  canceling: 'cancelled',
  canceled: 'cancelled',
};
const _toLegacyStatus = (s) => {
  const k = (s || '').toLowerCase();
  if (k in _DAGSTER_STATUS_TO_LEGACY) {return _DAGSTER_STATUS_TO_LEGACY[k];}
  // Empty status (Dagster hasn't accepted the run yet) still means
  // "just queued"; any other unrecognised string is a real anomaly.
  return k === '' ? 'queued' : 'unknown';
};

// Runs older than this without a terminal status are treated as stuck
// — Dagster crashed, the daemon was killed, or the run got orphaned.
// The UI surfaces a "Stale" badge instead of an indefinitely spinning
// "Running…". Tunable via REACT_APP_STALE_RUN_MINUTES.
const DEFAULT_STALE_RUN_MINUTES = 90;
const _STALE_RUN_MINUTES = (() => {
  const v = Number(process.env.REACT_APP_STALE_RUN_MINUTES);
  return Number.isFinite(v) && v > 0 ? v : DEFAULT_STALE_RUN_MINUTES;
})();

// If a staging run has been "success" for this long with no bronze
// run found, we assume there is no bronze layer for the connector
// (or the bronze sensor is wedged) and surface SUCCESS instead of
// leaving the card stuck on "Source data synced — preparing transform".
// The bronze sensor polls every ~30s so a 10-min cap is generous.
const _BRONZE_GRACE_MINUTES = (() => {
  const v = Number(process.env.REACT_APP_BRONZE_GRACE_MINUTES);
  return Number.isFinite(v) && v > 0 ? v : 10;
})();

const _isStale = (startIso, status) => {
  if (status !== 'running' && status !== 'queued') {return false;}
  if (!startIso) {return false;}
  const ageMs = Date.now() - new Date(startIso).getTime();
  return ageMs > _STALE_RUN_MINUTES * 60 * 1000;
};

// Has the staging run been finished long enough that we should stop
// waiting for a bronze run that may never come?
const _bronzeGraceExpired = (stagingEndIso) => {
  if (!stagingEndIso) {return false;}
  const ageMs = Date.now() - new Date(stagingEndIso).getTime();
  return ageMs > _BRONZE_GRACE_MINUTES * 60 * 1000;
};

const _stripConnectorPrefix = (assetPath, connector) => {
  // Asset keys are written as `<connector>__<stream>` in
  // orchestrator/assets/base.py:_safe_asset_key. Strip the prefix when
  // present so the UI can match against `connection.streams[].name`.
  const flat = Array.isArray(assetPath) ? assetPath.join('.') : String(assetPath || '');
  const norm = (connector || '').replace(/-/g, '_');
  const prefix = `${norm}__`;
  return flat.startsWith(prefix) ? flat.slice(prefix.length) : flat;
};

// Phase keys exposed to the UI. The card / table render these into
// human strings — keep them stable so badge colours don't churn.
//
//   queued        — accepted by the API, not yet started by Dagster
//   extracting    — staging asset is running (live connector pulls)
//   extracted     — staging done, waiting on the bronze sensor to fire
//   transforming  — bronze dbt build is running
//   success       — staging + bronze both done
//   failed        — anything failed (which layer is described in `phase_detail`)
//   partial       — multi-stream run where some streams succeeded
//   cancelled
//   unknown
//   stale         — see _isStale
const PHASE = Object.freeze({
  QUEUED: 'queued',
  EXTRACTING: 'extracting',
  EXTRACTED: 'extracted',
  TRANSFORMING: 'transforming',
  SUCCESS: 'success',
  FAILED: 'failed',
  PARTIAL: 'partial',
  CANCELLED: 'cancelled',
  UNKNOWN: 'unknown',
  STALE: 'stale',
});

// Standalone phase derivation for the staging-only view (the source-card
// and run-list rows where we haven't fetched the bronze run yet).
const _stagingPhase = (legacyStatus, stale) => {
  if (stale) {return PHASE.STALE;}
  if (legacyStatus === 'failed') {return PHASE.FAILED;}
  if (legacyStatus === 'success') {return PHASE.EXTRACTED;}
  if (legacyStatus === 'cancelled') {return PHASE.CANCELLED;}
  if (legacyStatus === 'unknown') {return PHASE.UNKNOWN;}
  if (legacyStatus === 'queued') {return PHASE.QUEUED;}
  // running. We don't try to distinguish "writing to warehouse" vs
  // "extracting" (whether or not there's already a materialization)
  // because the warehouse write phase is typically <1s and indistinct
  // in the asset event stream.
  return PHASE.EXTRACTING;
};

const _adaptDagsterRun = (r) => {
  const status = _toLegacyStatus(r.status);
  const start = r.start_time || r.creation_time;
  const stale = _isStale(start, status);
  const finalStatus = stale ? 'failed' : status;
  const phase = _stagingPhase(finalStatus, stale);
  return {
    id: r.id,
    connection_id: r.connection_id,
    connector_name: r.connector,
    // A stale active run is shown as failed in the legacy shape so
    // downstream UI (badge color, "is this run blocking?") treats it
    // as a clear terminal state. The `stale` flag below lets components
    // render a distinct "Stale" tooltip when they care.
    status: finalStatus,
    stale,
    raw_status: r.status,
    started_at: start,
    finished_at: r.end_time,
    rows_loaded: 0, // Dagster runs don't track row counts at this layer.
    error: stale
      ? `Run has been ${status} for over ${_STALE_RUN_MINUTES} minutes — orchestrator may be down.`
      : null,
    triggered_by: r.triggered_by,
    phase,
    // Surface the original Dagster shape for components that want it.
    _dagster: r,
  };
};

// Human label for a phase. Centralised so the card and the per-stream
// table render the same words.
export const phaseLabel = (phase) => {
  switch (phase) {
    case PHASE.QUEUED:        return 'Waiting in queue';
    case PHASE.EXTRACTING:    return 'Syncing data from source';
    case PHASE.EXTRACTED:     return 'Source data synced — preparing transform';
    case PHASE.TRANSFORMING:  return 'Transforming data';
    case PHASE.SUCCESS:       return 'Done';
    case PHASE.FAILED:        return 'Failed';
    case PHASE.PARTIAL:       return 'Partial success';
    case PHASE.CANCELLED:     return 'Cancelled';
    case PHASE.STALE:         return 'Stale — no orchestrator update';
    default:                  return '';
  }
};

export { PHASE };

// Drop-in replacement for `listSyncs(connectionId)`. Returns an array of
// run rows in the legacy shape, sorted desc by start_time (mirrors the
// `runsOrError` GraphQL ordering).
//
// To make the source-card phase indicator mirror the right-side detail
// (which knows about the bronze transform run), this also fetches all
// bronze runs for the workspace in a single extra call and stitches in
// the transform phase per matching staging run. The cost is one fixed
// extra Dagster GraphQL query per poll — far cheaper than per-card
// lookups.
// Index bronze runs by their `triggered_by` tag (set to
// `staging_run:<staging_id>` by the run-status sensor). Keeps the most
// recent if multiple bronze runs were spawned for the same staging run
// (e.g. a manual rerun).
function _indexBronzeRunsByStagingId(bronzeRuns) {
  const bronzeByStaging = new Map();
  for (const b of bronzeRuns || []) {
    const key = (b.tags || {}).triggered_by;
    if (!key || !key.startsWith('staging_run:')) {continue;}
    const stagingId = key.slice('staging_run:'.length);
    const prev = bronzeByStaging.get(stagingId);
    if (!prev || (b.creation_time || '') > (prev.creation_time || '')) {
      bronzeByStaging.set(stagingId, b);
    }
  }
  return bronzeByStaging;
}

// Bronze phase once staging has succeeded — mirrors the if/else-if chain
// this replaces exactly (order matters: first match wins).
function _combinedPhaseForSuccess(bStatus, bStale) {
  if (bStale) {return { phase: PHASE.STALE, phaseDetail: null };}
  if (bStatus === 'failed') {return { phase: PHASE.FAILED, phaseDetail: 'transform failed' };}
  if (bStatus === 'cancelled') {return { phase: PHASE.CANCELLED, phaseDetail: null };}
  if (bStatus === 'success') {return { phase: PHASE.SUCCESS, phaseDetail: null };}
  if (bStatus === 'queued') {return { phase: PHASE.EXTRACTED, phaseDetail: null };}
  return { phase: PHASE.TRANSFORMING, phaseDetail: null };
}

// Combine one staging run with its matching bronze run (if any) into the
// same phase machine the detail view uses, so the card and the right pane
// agree on what the pipeline is doing.
function _mergeStagingWithBronze(adapted, bronze) {
  if (!bronze) {
    // No bronze run yet. If the staging finished long enough ago that the
    // bronze sensor *should* have fired by now, assume the connector has no
    // bronze layer (or the sensor is wedged) and resolve to SUCCESS rather
    // than leaving the card stuck on "Source data synced — preparing
    // transform" indefinitely.
    if (adapted.status === 'success' && _bronzeGraceExpired(adapted.finished_at)) {
      return { ...adapted, phase: PHASE.SUCCESS };
    }
    return adapted;
  }
  const bStatus = _toLegacyStatus(bronze.status);
  const bStale = _isStale(bronze.start_time || bronze.creation_time, bStatus);
  const { phase, phaseDetail } = adapted.status === 'success'
    ? _combinedPhaseForSuccess(bStatus, bStale)
    : { phase: adapted.phase, phaseDetail: null };
  return {
    ...adapted,
    phase,
    phase_detail: phaseDetail,
    bronze_run_id: bronze.id,
    bronze_status: bStatus,
  };
}

export const listSyncsViaDagster = async (connectionId) => {
  const [stagingRuns, bronzeRuns] = await Promise.all([
    listDagitRuns({ connectionId, limit: 50 }),
    // Bronze runs for this workspace — `layer=bronze` is the tag the
    // bronze sensor stamps. Don't filter by connectionId because bronze
    // runs are tagged tenant/workspace/connector/layer but inherit the
    // staging run's connection_id only via `triggered_by`.
    listDagitRuns({ layer: 'bronze', connector: null, limit: 200 }).catch(() => []),
  ]);

  const bronzeByStaging = _indexBronzeRunsByStagingId(bronzeRuns);
  return (stagingRuns || []).map((r) =>
    _mergeStagingWithBronze(_adaptDagsterRun(r), bronzeByStaging.get(r.id)));
};

// Drop-in replacement for `getSyncRun(id)`. Returns the run plus a
// `streams[]` array shaped like the legacy per-stream rows so the
// existing UI just works.
//
// Stream list assembly, in order:
//   1. `assetSelection` — explicit per-stream/connection asset list.
//   2. `assetMaterializations` — assets that successfully wrote.
//   3. `fallbackStreamNames` (e.g. connection.selected_streams) — used
//      ONLY when both above are empty so the table never renders blank
//      after a successful run whose snapshot didn't carry an explicit
//      asset selection (legacy runs, scheduled materializations, etc.).
//
// `rows_loaded` / `requests_made` come from each materialization's
// `metadata` map (asset emits `Output(rows, metadata={"rows_loaded": ...})`).
function _indexMaterializationsByStream(materializations, connector) {
  const matByStream = new Map();
  for (const m of materializations || []) {
    const name = _stripConnectorPrefix(m.asset_key, connector);
    const prev = matByStream.get(name);
    if (!prev || (m.timestamp || '') > (prev.timestamp || '')) {
      matByStream.set(name, m);
    }
  }
  return matByStream;
}

// Stream list assembly, in order:
//   1. `assetSelection` — explicit per-stream/connection asset list.
//   2. `matByStream` keys — assets that successfully wrote.
//   3. `fallbackStreamNames` (e.g. connection.selected_streams) — used
//      ONLY when both above are empty so the table never renders blank
//      after a successful run whose snapshot didn't carry an explicit
//      asset selection (legacy runs, scheduled materializations, etc.).
function _assembleStreamNames(assetSelection, connector, matByStream, fallbackStreamNames) {
  const seen = new Set();
  const streamNames = [];
  for (const path of assetSelection || []) {
    const name = _stripConnectorPrefix(path, connector);
    if (!seen.has(name)) { seen.add(name); streamNames.push(name); }
  }
  for (const name of matByStream.keys()) {
    if (!seen.has(name)) { seen.add(name); streamNames.push(name); }
  }
  if (streamNames.length === 0) {
    for (const name of fallbackStreamNames) {
      if (!seen.has(name)) { seen.add(name); streamNames.push(name); }
    }
  }
  return streamNames;
}

// Per-stream status: a successful materialization is the strongest signal —
// if we have one, the stream succeeded even if the run as a whole failed
// afterwards. Without a materialization, a TERMINAL run status means this
// stream specifically didn't emit (surfaced as 'skipped', not 'failed', so
// users don't think the connector crashed for that stream); otherwise the
// stream just inherits the run's still-in-flight status.
function _streamStatus(mat, baseStatus) {
  if (mat) {return 'success';}
  if (baseStatus === 'success' || baseStatus === 'failed') {
    return baseStatus === 'failed' ? 'skipped' : 'success';
  }
  return baseStatus;
}

// `rows_loaded` / `requests_made` come from each materialization's
// `metadata` map (asset emits `Output(rows, metadata={"rows_loaded": ...})`).
function _shapeStreamRow(name, matByStream, base) {
  const mat = matByStream.get(name);
  const meta = (mat && mat.metadata) || {};
  return {
    stream: name,
    status: _streamStatus(mat, base.status),
    started_at: base.started_at,
    finished_at: mat ? mat.timestamp : base.finished_at,
    rows_loaded: Number(meta.rows_loaded || 0),
    requests_made: Number(meta.requests_made || 0),
  };
}

// Run-level outcome: if some streams succeeded and some didn't, mark as
// partial_success. This makes the badge color amber (warning) and makes the
// failure mode obvious to users at a glance, rather than "the whole sync
// failed" when in fact most data did land.
function _resolveRunStatus(baseStatus, streams) {
  if (baseStatus !== 'failed') {return baseStatus;}
  const successCount = streams.filter((s) => s.status === 'success').length;
  const skippedCount = streams.filter((s) => s.status === 'skipped').length;
  return successCount > 0 && skippedCount + successCount === streams.length
    ? 'partial_success' : baseStatus;
}

// Only look up the bronze run for live runs and recently-finished ones —
// old completed runs (>1h) skip the lookup to keep the polling loop cheap.
function _shouldLookupBronzeRun(base, ageMs) {
  if (!base.connector_name) {return false;}
  if (base.status === 'running' || base.status === 'queued') {return true;}
  return ageMs > 0 && ageMs < 60 * 60 * 1000;
}

// Pull the bronze (transformation) run that the
// bronze_<connector>_after_staging sensor spawned for this staging run, if
// any. This drives the "Transforming data" phase.
async function _lookupBronzeRunForStaging(runId, base) {
  const ageMs = base.started_at ? Date.now() - new Date(base.started_at).getTime() : 0;
  if (!_shouldLookupBronzeRun(base, ageMs)) {return null;}
  try {
    // Filter by triggered_by ONLY — don't add connector. Staging runs tag
    // connector with the hyphenated form ("github-copilot") but bronze runs
    // use the underscore form ("github_copilot") because they're keyed by
    // the dagster code-location name. Matching on both tags would always
    // miss; the triggered_by tag is unique per staging run id and is enough.
    const bronzeRuns = await listDagitRuns({
      layer: 'bronze', triggeredBy: `staging_run:${runId}`, limit: 1,
    });
    return (bronzeRuns && bronzeRuns[0]) || null;
  } catch (_e) {
    // Network/transient — fall through; phase just won't show transform.
    return null;
  }
}

// Bronze (transform) phase once staging has succeeded. Order matters —
// first match wins, mirroring the if/else-if chain this replaces.
function _bronzePhaseForSuccessfulStaging(bronzeRun, stagingFinishedAt) {
  if (!bronzeRun) {
    // No bronze run found. If staging finished long enough ago, the
    // connector likely has no bronze layer — resolve to SUCCESS so the
    // card / banner don't sit on "preparing transform" forever.
    return {
      phase: _bronzeGraceExpired(stagingFinishedAt) ? PHASE.SUCCESS : PHASE.EXTRACTED,
      phaseDetail: null,
    };
  }
  const bStatus = _toLegacyStatus(bronzeRun.status);
  const bStale = _isStale(bronzeRun.start_time || bronzeRun.creation_time, bStatus);
  if (bStale) {return { phase: PHASE.STALE, phaseDetail: null };}
  if (bStatus === 'failed') {return { phase: PHASE.FAILED, phaseDetail: 'transform failed' };}
  if (bStatus === 'cancelled') {return { phase: PHASE.CANCELLED, phaseDetail: null };}
  if (bStatus === 'success') {return { phase: PHASE.SUCCESS, phaseDetail: null };}
  if (bStatus === 'queued') {return { phase: PHASE.EXTRACTED, phaseDetail: null };} // bronze accepted but not running yet
  return { phase: PHASE.TRANSFORMING, phaseDetail: null };
}

// Run-status values whose detail-view phase doesn't depend on anything else.
const _RUN_STATUS_PHASE = {
  cancelled: PHASE.CANCELLED,
  partial_success: PHASE.PARTIAL,
  queued: PHASE.QUEUED,
  running: PHASE.EXTRACTING,
};

// Combined phase machine for the detail view. Order matters — the first
// matching branch wins, mirroring the original if/else-if chain exactly
// (these are all mutually-exclusive equality checks on `runStatus`, so the
// lookup table's iteration order doesn't change the result).
function _resolveDetailPhase(base, runStatus, bronzeRun) {
  if (base.stale) {return { phase: PHASE.STALE, phaseDetail: null };}
  if (runStatus === 'failed') {return { phase: PHASE.FAILED, phaseDetail: 'source sync failed' };}
  if (runStatus in _RUN_STATUS_PHASE) {return { phase: _RUN_STATUS_PHASE[runStatus], phaseDetail: null };}
  if (runStatus === 'success') {
    // Staging succeeded — defer to the bronze (transform) run state.
    return _bronzePhaseForSuccessfulStaging(bronzeRun, base.finished_at);
  }
  return { phase: PHASE.UNKNOWN, phaseDetail: null };
}

// Drop-in replacement for `getSyncRun(id)`. Returns the run plus a
// `streams[]` array shaped like the legacy per-stream rows so the
// existing UI just works.
export const getSyncRunViaDagster = async (runId, { fallbackStreamNames = [] } = {}) => {
  const r = await getDagitRun(runId);
  const base = _adaptDagsterRun(r);

  const matByStream = _indexMaterializationsByStream(r.materializations, r.connector);
  const streamNames = _assembleStreamNames(r.asset_selection, r.connector, matByStream, fallbackStreamNames);
  const streams = streamNames.map((name) => _shapeStreamRow(name, matByStream, base));

  const runStatus = _resolveRunStatus(base.status, streams);
  const bronzeRun = await _lookupBronzeRunForStaging(runId, base);
  const { phase, phaseDetail } = _resolveDetailPhase(base, runStatus, bronzeRun);

  return {
    ...base,
    status: runStatus,
    phase,
    phase_detail: phaseDetail,
    bronze_run_id: bronzeRun ? bronzeRun.id : null,
    streams,
  };
};

// ── Vault (per-tenant ingested data) ─────────────────────────────────
// The three metadata reads share one endpoint since 2026-08-19. The wrapper
// names and signatures are unchanged on purpose, so no component had to move.
export const listVaultSchemas = () => request('GET', '/vault/metadata?kind=schemas');
export const listVaultTables = (schema) =>
  request('GET', `/vault/metadata?kind=tables&schema=${encodeURIComponent(schema)}`);
export const getVaultTableSchema = (schema, table) =>
  request('GET', `/vault/metadata?kind=columns&schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}`);
export const getVaultErd = () => request('GET', '/vault/erd');
export const getVaultSchemaChanges = (sinceHours = 168) =>
  request('GET', `/vault/schema_changes?since_hours=${sinceHours}`);
export const getVaultData = (schema, table, limit = 50, offset = 0) =>
  request('GET', `/vault/data?schema=${encodeURIComponent(schema)}&table=${encodeURIComponent(table)}&limit=${limit}&offset=${offset}`);
// Legacy aliases (kept for back-compat with existing pages).
export const getVaultSchema = async () => {
  const schemas = await listVaultSchemas();
  return { schemas };
};
export const queryVault = (sql, rowLimit = 1000) =>
  request('POST', '/vault/query', { sql, row_limit: rowLimit });
export const getVaultQueryHistory = (limit = 50, mineOnly = false) =>
  request('GET', `/vault/query_history?limit=${limit}&mine_only=${mineOnly}`);

// Workspace data reset (destructive — UI must collect explicit confirmation).
export const getVaultResetPreview = () => request('GET', '/vault/reset/preview');
export const resetVault = (opts) => request('POST', '/vault/reset', opts);

// ── Tenants & workspaces ─────────────────────────────────────────────
